import {
  type ReactNode,
  type Ref,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useDropzone } from 'react-dropzone';

import Text from '@cartwhl/ui/base/Text';

export type FilesListFile = {
  id: string;
  file: File;
  objectUrl: string;
};

/** Imperative handle for controlling FilesList from parent */
export type FilesListHandle = {
  /** Add files programmatically (e.g., from external drag-drop) */
  addFiles: (files: File[]) => void;
  /** Clear all files */
  clearAll: () => void;
  /** Get current files */
  getFiles: () => FilesListFile[];
  /** Open the native file picker (same as built-in “add more”) */
  openFilePicker: () => void;
};

export type FilesListProps = {
  /** Allowed MIME types, e.g., ['video/mp4', 'video/quicktime'] */
  allowedMimeTypes: string[];
  /**
   * When the browser reports an empty file type (common for some .fbx), accept
   * files whose names end with one of these extensions (lowercase, with dot).
   */
  fallbackExtensions?: string[];
  /** Render function for each file item */
  renderItem: (file: FilesListFile, removeFile: () => void) => ReactNode;
  /** Callback fired when files are added or removed */
  onFilesChange?: (files: FilesListFile[]) => void;
  /** Custom empty state content (optional) */
  emptyStateContent?: ReactNode;
  /**
   * Content to show in the full-page drag overlay.
   * If provided, enables the drag overlay feature.
   * If not provided, no overlay is shown.
   */
  dragOverlayContent?: ReactNode;
  /** Max number of files. When reached, hides the "add more" button. If 1, replaces existing file when adding. */
  maxFiles?: number;
  /** Height in px for the dropzone/list. Default 341. Use smaller value for compact layouts. */
  height?: number;
  /** When true, the filled list uses a fixed height (instead of minHeight) and scrolls internally. */
  constrainHeight?: boolean;
  /** When true, the dropzone uses minHeight instead of height and grows to fill its flex container. */
  fillContainer?: boolean;
  /** Override the entire filled-state layout. Receives files, a remove callback, and an add-more trigger. */
  renderFileList?: (
    files: FilesListFile[],
    removeFile: (id: string) => void,
    addMore: () => void,
  ) => ReactNode;
  /** Ref for imperative handle */
  ref?: Ref<FilesListHandle>;
  /** When true, the default “select more files” bar is hidden (use `openFilePicker` from ref for a custom control). */
  hideBuiltInAddMore?: boolean;
  /** Empty-state chrome: dashed dropzone (default) or plain container for custom filled lists. */
  emptyStateVariant?: 'dropzone' | 'plain';
  /** When true, clicking the dropzone does not open the native file picker. */
  disableClick?: boolean;
};

function normalizeExtension(ext: string) {
  const e = ext.startsWith('.') ? ext : `.${ext}`;
  return e.toLowerCase();
}

/**
 * Maps each allowed MIME to extensions the OS file picker understands.
 * MIME-only accept often hides .glb / .gltf / .fbx in native pickers.
 */
function getAcceptConfig(
  allowedMimeTypes: string[],
  fallbackExtensions?: string[],
): Record<string, string[]> {
  const accept: Record<string, string[]> = {};
  for (const mimeType of allowedMimeTypes) {
    accept[mimeType] = [];
  }

  if (!fallbackExtensions?.length) {
    return accept;
  }

  const exts = fallbackExtensions.map(normalizeExtension);

  const add = (mime: string, extension: string) => {
    if (!accept[mime]) return;
    if (!accept[mime].includes(extension)) {
      accept[mime].push(extension);
    }
  };

  for (const ext of exts) {
    if (ext === '.glb') {
      add('model/gltf-binary', '.glb');
      add('application/octet-stream', '.glb');
    } else if (ext === '.gltf') {
      add('model/gltf+json', '.gltf');
      add('application/octet-stream', '.gltf');
    } else if (ext === '.fbx') {
      add('application/octet-stream', '.fbx');
    } else {
      for (const mime of allowedMimeTypes) {
        add(mime, ext);
      }
    }
  }

  return accept;
}

/** Comma-separated `accept` for the fallback `<input type="file">` (add-more). */
function getNativeFileInputAccept(
  allowedMimeTypes: string[],
  fallbackExtensions?: string[],
): string {
  const parts = [...allowedMimeTypes];
  if (fallbackExtensions?.length) {
    for (const e of fallbackExtensions) {
      const ext = normalizeExtension(e);
      if (!parts.includes(ext)) parts.push(ext);
    }
  }
  return parts.join(',');
}

const DEFAULT_HEIGHT = 341;

function fileAllowedByExtension(
  file: File,
  fallbackExtensions?: string[],
): boolean {
  if (!fallbackExtensions?.length) return false;
  const lower = file.name.toLowerCase();
  return fallbackExtensions.some((e) =>
    lower.endsWith(normalizeExtension(e)),
  );
}

function mimeTypeMatches(actualMimeType: string, allowedMimeType: string) {
  if (actualMimeType === allowedMimeType) return true;

  const [allowedType, allowedSubtype] = allowedMimeType.split('/');
  const [actualType] = actualMimeType.split('/');

  return allowedSubtype === '*' && allowedType === actualType;
}

function didDropWithinElement(event: DragEvent, element: HTMLElement | null) {
  if (!element) return false;
  if (event.target instanceof Node && element.contains(event.target)) return true;

  const rect = element.getBoundingClientRect();

  return (
    event.clientX >= rect.left &&
    event.clientX <= rect.right &&
    event.clientY >= rect.top &&
    event.clientY <= rect.bottom
  );
}

function isAcceptedFile(
  file: File,
  allowedMimeTypes: string[],
  fallbackExtensions?: string[],
): boolean {
  if (allowedMimeTypes.some((mimeType) => mimeTypeMatches(file.type, mimeType))) {
    return true;
  }
  return fileAllowedByExtension(file, fallbackExtensions);
}

export default function FilesList({
  allowedMimeTypes,
  fallbackExtensions,
  renderItem,
  onFilesChange,
  emptyStateContent,
  dragOverlayContent,
  maxFiles,
  height = DEFAULT_HEIGHT,
  constrainHeight = false,
  fillContainer = false,
  hideBuiltInAddMore = false,
  emptyStateVariant = 'dropzone',
  disableClick = false,
  renderFileList,
  ref,
}: FilesListProps) {
  const isCompact = height < DEFAULT_HEIGHT;
  const [files, setFiles] = useState<FilesListFile[]>([]);
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const listContainerRef = useRef<HTMLDivElement>(null);
  const dropzoneRef = useRef<HTMLDivElement>(null);
  const prevFilesCountRef = useRef(files.length);
  const dragCounterRef = useRef(0);
  const dropHandledByDropzoneRef = useRef(false);

  // Scroll to bottom only when new files are added (not removed)
  useEffect(() => {
    if (
      listContainerRef.current &&
      files.length > 0 &&
      files.length > prevFilesCountRef.current
    ) {
      listContainerRef.current.scrollTo({
        top: listContainerRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
    prevFilesCountRef.current = files.length;
  }, [files.length]);

  // Notify parent when files change
  useEffect(() => {
    onFilesChange?.(files);
  }, [files, onFilesChange]);

  const addFiles = useCallback(
    (newFiles: File[]) => {
      const validFiles = newFiles.filter((file) =>
        isAcceptedFile(file, allowedMimeTypes, fallbackExtensions),
      );

      const fileInfos: FilesListFile[] = validFiles.map((file) => ({
        id: crypto.randomUUID(),
        file,
        objectUrl: URL.createObjectURL(file),
      }));

      setFiles((prev) => {
        let next: FilesListFile[];

        if (maxFiles === 1) {
          next = fileInfos.slice(0, 1);
        } else if (maxFiles != null) {
          const combined = [...prev, ...fileInfos];
          next = combined.slice(0, maxFiles);
        } else {
          next = [...prev, ...fileInfos];
        }

        const nextIds = new Set(next.map((f) => f.id));
        for (const f of prev) {
          if (!nextIds.has(f.id)) URL.revokeObjectURL(f.objectUrl);
        }
        for (const f of fileInfos) {
          if (!nextIds.has(f.id)) URL.revokeObjectURL(f.objectUrl);
        }

        return next;
      });
    },
    [allowedMimeTypes, fallbackExtensions, maxFiles],
  );

  const removeFile = useCallback((id: string) => {
    setFiles((prev) => {
      const file = prev.find((f) => f.id === id);
      if (file) {
        URL.revokeObjectURL(file.objectUrl);
      }
      return prev.filter((f) => f.id !== id);
    });
  }, []);

  const clearAll = useCallback(() => {
    setFiles((prev) => {
      for (const file of prev) {
        URL.revokeObjectURL(file.objectUrl);
      }
      return [];
    });
  }, []);

  const handleFileSelect = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Expose imperative methods to parent
  useImperativeHandle(ref, () => ({
    addFiles: (newFiles: File[]) => addFiles(newFiles),
    clearAll,
    getFiles: () => files,
    openFilePicker: handleFileSelect,
  }));

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      dropHandledByDropzoneRef.current = true;
      queueMicrotask(() => {
        dropHandledByDropzoneRef.current = false;
      });

      if (acceptedFiles.length > 0) {
        addFiles(acceptedFiles);
      }
    },
    [addFiles],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: getAcceptConfig(allowedMimeTypes, fallbackExtensions),
    multiple: true,
    noClick:
      disableClick ||
      emptyStateVariant === 'plain' ||
      (files.length > 0 && maxFiles !== 1),
  });

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      addFiles(Array.from(e.target.files));
      // Reset input so same file can be selected again
      e.target.value = '';
    }
  };

  // Document-level drag handlers for full-viewport overlay (only when dragOverlayContent is provided)
  useEffect(() => {
    if (!dragOverlayContent) return;

    // Check if any of the dragged items match the allowed MIME types
    const hasMatchingFiles = (dataTransfer: DataTransfer | null): boolean => {
      if (!dataTransfer?.items) return false;
      for (const item of dataTransfer.items) {
        if (item.kind !== 'file') continue;
        if (allowedMimeTypes.some((mimeType) => mimeTypeMatches(item.type, mimeType))) {
          return true;
        }
        const f = item.getAsFile();
        if (f && isAcceptedFile(f, allowedMimeTypes, fallbackExtensions)) {
          return true;
        }
      }
      return false;
    };

    const handleDragEnter = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current++;
      if (dragCounterRef.current === 1 && hasMatchingFiles(e.dataTransfer)) {
        setIsDraggingOver(true);
      }
    };

    const handleDragLeave = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDraggingOver(false);
      }
    };

    const handleDragOver = (e: DragEvent) => {
      e.preventDefault();
    };

    const handleDrop = (e: DragEvent) => {
      e.preventDefault();
      dragCounterRef.current = 0;
      setIsDraggingOver(false);

      if (dropHandledByDropzoneRef.current) {
        dropHandledByDropzoneRef.current = false;
        return;
      }

      // Skip if drop happened within the dropzone (it will be handled by useDropzone)
      if (didDropWithinElement(e, dropzoneRef.current)) {
        return;
      }

      if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
        addFiles(Array.from(e.dataTransfer.files));
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
    };
  }, [dragOverlayContent, addFiles, allowedMimeTypes, fallbackExtensions]);

  const defaultEmptyState = (
    <div className="flex items-center gap-1">
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 2.5V17.5M10 2.5L6.25 6.25M10 2.5L13.75 6.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-blue"
        />
      </svg>
      <Text variant="studio/body/medium/sm">
        <span className="text-blue">Select file(s)</span>{' '}
        <span className="text-gray-60">or drop them here</span>
      </Text>
    </div>
  );

  const showAddMore =
    !hideBuiltInAddMore &&
    files.length > 0 &&
    (maxFiles == null || files.length < maxFiles);

  const addMoreButton = showAddMore ? (
    <button
      type="button"
      onClick={handleFileSelect}
      className={`bg-secondary hover:bg-gray-30 flex shrink-0 items-center justify-center gap-2 rounded-lg transition-colors ${isCompact ? 'py-1' : 'py-3'}`}
    >
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M10 2.5V17.5M10 2.5L6.25 6.25M10 2.5L13.75 6.25"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="text-blue"
        />
      </svg>
      <Text variant="studio/body/medium/sm">
        <span className="text-blue">Select file(s)</span>{' '}
        <span className="text-gray-60">or drop them here</span>
      </Text>
    </button>
  ) : null;

  /** Filled list scrolls inside a bounded flex region (fixed height or fillContainer parent). */
  const fileListScrollsInFlex = constrainHeight || fillContainer;

  const fileList = (
    <div
      ref={listContainerRef}
      className={`bg-secondary border-primary styled-scrollbar flex flex-col overflow-y-auto rounded-lg border ${
        fileListScrollsInFlex ? 'min-h-0 h-full flex-1' : ''
      } ${isDragActive ? 'ring-blue ring-2' : ''}`}
      style={fileListScrollsInFlex ? undefined : { minHeight: height }}
    >
      <div
        className={`divide-gray-30 flex flex-col divide-y ${isCompact ? 'px-2 py-1' : 'px-6 py-2'}`}
      >
        {files.map((file) => (
          <div key={file.id} className={isCompact ? 'py-1' : 'py-3'}>
            {renderItem(file, () => removeFile(file.id))}
          </div>
        ))}
      </div>
    </div>
  );

  const isPlainEmptyState = emptyStateVariant === 'plain';

  const content = (
    <div className={`flex flex-col gap-2 ${fillContainer ? 'min-h-0 flex-1' : ''}`}>
      {/* Hidden file input for "add more" functionality */}
      <input
        ref={fileInputRef}
        type="file"
        accept={getNativeFileInputAccept(allowedMimeTypes, fallbackExtensions)}
        multiple
        className="hidden"
        onChange={handleFileInputChange}
      />

      {files.length === 0 ? (
        <div ref={dropzoneRef} className={fillContainer ? 'flex min-h-0 flex-1 flex-col' : ''}>
          <div {...getRootProps()} className={fillContainer ? 'flex min-h-0 flex-1 flex-col' : ''}>
            <input {...getInputProps()} />
            <div
              className={
                isPlainEmptyState
                  ? `flex flex-col transition-colors ${
                      isDragActive ? 'ring-blue rounded-lg ring-2' : ''
                    }`
                  : `border-blue flex cursor-pointer flex-col items-center justify-center gap-1 border-2 border-dashed transition-colors ${
                      isCompact ? 'rounded-lg p-2' : 'rounded-2xl p-4'
                    } ${isDragActive ? 'bg-blue/20' : 'bg-blue/10 hover:bg-blue/15'} ${fillContainer ? 'flex-1' : ''}`
              }
              style={
                isPlainEmptyState
                  ? undefined
                  : fillContainer
                    ? { minHeight: height }
                    : { height }
              }
              onClick={
                isPlainEmptyState
                  ? (event) => {
                      event.stopPropagation();
                    }
                  : undefined
              }
            >
              {emptyStateContent ?? defaultEmptyState}
            </div>
          </div>
        </div>
      ) : renderFileList ? (
        <div ref={dropzoneRef}>
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            {renderFileList(files, removeFile, handleFileSelect)}
          </div>
        </div>
      ) : constrainHeight || fillContainer ? (
        <div
          className={`flex min-h-0 flex-col gap-2 ${fillContainer ? 'flex-1' : ''}`}
          style={constrainHeight ? { height } : undefined}
        >
          <div ref={dropzoneRef} className="min-h-0 flex-1">
            <div {...getRootProps()} className="h-full">
              <input {...getInputProps()} />
              {fileList}
            </div>
          </div>
          {addMoreButton}
        </div>
      ) : (
        <>
          <div ref={dropzoneRef}>
            <div {...getRootProps()}>
              <input {...getInputProps()} />
              {fileList}
            </div>
          </div>
          {addMoreButton}
        </>
      )}
    </div>
  );

  return (
    <div
      className={
        fillContainer
          ? 'flex min-h-0 flex-1 flex-col'
          : 'contents'
      }
    >
      {content}

      {/* Full-viewport drag overlay */}
      {isDraggingOver && dragOverlayContent && (
        <div className="border-blue bg-blue/40 backdrop-blur-xs pointer-events-none fixed inset-5 z-50 flex items-center justify-center rounded-2xl border-2 border-dashed">
          {dragOverlayContent}
        </div>
      )}
    </div>
  );
}
